import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  IconButton, Stack, Chip, Grid, Tab, Tabs, FormControlLabel, Switch, Alert,
  List, ListItemButton, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Warning as WarningIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { contextApi } from '../api/services';
import type { ExecutiveContext, ContextLayer } from '../types/models';

const layers: ContextLayer[] = ['Profile', 'Strategic', 'Weekly', 'Daily', 'AnnualStrategy'];

const layerSuggestions: Record<string, Array<{ key: string; value: string }>> = {
  Profile: [
    { key: 'Leadership Style', value: 'I prefer direct, data-driven communication with clear action items' },
    { key: 'Decision Style', value: 'I want options presented with a recommendation, not open-ended' },
    { key: 'Communication Preferences', value: 'Brief updates over email, detailed discussion in person' },
    { key: 'Working Style', value: 'Deep work mornings, meetings afternoons' },
  ],
  Strategic: [
    { key: 'Q2 Focus', value: 'Customer retention and product-led growth' },
    { key: 'Board Priority', value: 'Revenue growth 20% YoY, margin improvement' },
    { key: 'Risk Awareness', value: 'Regulatory changes in Q3, key person dependency' },
  ],
  Weekly: [
    { key: "This Week's Focus", value: 'Finalise Q2 board pack, review vendor shortlist' },
    { key: 'Key Meetings', value: 'Board prep Monday, all-hands Wednesday' },
    { key: 'Blockers', value: 'Waiting on legal review for contract' },
  ],
  Daily: [
    { key: "Today's Priority", value: 'Complete strategy review, send board update' },
    { key: 'Energy Level', value: 'High energy morning, flagging after lunch' },
    { key: 'Context for EA', value: 'Running late from school drop-off, push 9am to 9:15' },
  ],
};

const layerColors: Record<ContextLayer, string> = {
  Profile: '#6C9BFF',
  Strategic: '#69F0AE',
  Weekly: '#FFB74D',
  Daily: '#FF8A65',
  AnnualStrategy: '#CE93D8',
};

const layerDescriptions: Record<ContextLayer, string> = {
  Profile: 'Your leadership identity, management style, and communication preferences',
  Strategic: 'Quarterly and annual priorities that guide planning decisions',
  Weekly: 'This week\'s focus areas, key meetings, and short-term objectives',
  Daily: 'Today\'s specific context, energy level, and immediate concerns',
  AnnualStrategy: 'Long-term strategic themes and organisational direction for the year',
};

function isExpiringSoon(expiryDate?: string): boolean {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  const now = new Date();
  const diff = expiry.getTime() - now.getTime();
  const days = diff / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= 7;
}

function isExpired(expiryDate?: string): boolean {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
}

export default function ContextPage() {
  const [contexts, setContexts] = useState<ExecutiveContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCtx, setEditCtx] = useState<Partial<ExecutiveContext> | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [activeOnly, setActiveOnly] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardLayer, setWizardLayer] = useState<string>('Profile');

  const loadContexts = async () => {
    setLoading(true);
    try {
      setContexts(await contextApi.getAll());
    } catch (err) {
      console.error('Failed to load contexts', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContexts(); }, []);

  const handleSave = async () => {
    if (!editCtx?.key || !editCtx?.value) return;
    try {
      if (editCtx.id) {
        await contextApi.update(editCtx.id, editCtx);
      } else {
        await contextApi.create(editCtx);
      }
      setDialogOpen(false);
      setEditCtx(null);
      loadContexts();
    } catch (err) {
      console.error('Failed to save context', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this context entry?')) return;
    try {
      await contextApi.delete(id);
      loadContexts();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleWizardSelect = (suggestion: { key: string; value: string }) => {
    setEditCtx({ layer: wizardLayer as ContextLayer, key: suggestion.key, value: suggestion.value, isActive: true });
    setWizardOpen(false);
    setDialogOpen(true);
  };

  // Get the current layer for quick-add templates
  const currentLayer = tabValue > 0 ? layers[tabValue - 1] : null;
  const quickAddTemplates = currentLayer && layerSuggestions[currentLayer] ? layerSuggestions[currentLayer] : null;

  const baseFiltered = tabValue === 0
    ? contexts
    : contexts.filter(c => c.layer === layers[tabValue - 1]);
  const filteredContexts = activeOnly
    ? baseFiltered.filter(c => c.isActive)
    : baseFiltered;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4">Context Engine</Typography>
          <Typography variant="subtitle1">Executive context used in all AI planning calls</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setWizardOpen(true)}>
            Get Started
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
            setEditCtx({ layer: 'Daily', key: '', value: '', isActive: true });
            setDialogOpen(true);
          }}>
            Add Context
          </Button>
        </Stack>
      </Box>

      <Alert severity="info" icon={false} sx={{ mb: 3 }}>
        <Typography variant="body2">
          The Context Engine feeds into AI planning. The more context you provide, the better Cadence plans your day.
          Strategic context shapes weekly planning. Daily context adjusts today's schedule.
        </Typography>
      </Alert>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1 }}>
        <Tab label={`All (${contexts.length})`} />
        {layers.map(l => (
          <Tab key={l} label={`${l} (${contexts.filter(c => c.layer === l).length})`} />
        ))}
      </Tabs>

      {/* Layer description */}
      {tabValue > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
          {layerDescriptions[layers[tabValue - 1]]}
        </Typography>
      )}

      {/* Active only toggle and quick-add templates */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        {quickAddTemplates ? (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Quick-add template</InputLabel>
            <Select
              value=""
              label="Quick-add template"
              onChange={e => {
                const tpl = quickAddTemplates.find(t => t.key === e.target.value);
                if (tpl) {
                  setEditCtx({ layer: currentLayer as ContextLayer, key: tpl.key, value: tpl.value, isActive: true });
                  setDialogOpen(true);
                }
              }}
            >
              {quickAddTemplates.map(tpl => (
                <MenuItem key={tpl.key} value={tpl.key}>{tpl.key}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : <Box />}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={activeOnly}
              onChange={e => setActiveOnly(e.target.checked)}
            />
          }
          label={<Typography variant="caption" color="text.secondary">Active only</Typography>}
        />
      </Box>

      <Grid container spacing={2}>
        {filteredContexts.map((ctx) => (
          <Grid size={{ xs: 12, md: 6 }} key={ctx.id}>
            <Card sx={{ borderLeft: 3, borderColor: layerColors[ctx.layer] || 'primary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip label={ctx.layer} size="small" sx={{ bgcolor: `${layerColors[ctx.layer]}20`, color: layerColors[ctx.layer] }} />
                      <Typography variant="subtitle2">{ctx.key}</Typography>
                      {!ctx.isActive && (
                        <Chip label="Inactive" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', color: 'text.secondary' }} />
                      )}
                      {isExpiringSoon(ctx.expiryDate) && (
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                          label={`Expires ${new Date(ctx.expiryDate!).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.65rem', bgcolor: 'rgba(255, 183, 77, 0.15)', color: '#FFB74D', '& .MuiChip-icon': { color: '#FFB74D' } }}
                        />
                      )}
                      {isExpired(ctx.expiryDate) && (
                        <Chip
                          label="Expired"
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255, 82, 82, 0.15)', color: '#FF5252' }}
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{ctx.value}</Typography>
                  </Box>
                  <Stack direction="row">
                    <IconButton size="small" onClick={() => { setEditCtx(ctx); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(ctx.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editCtx?.id ? 'Edit Context' : 'New Context'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Layer</InputLabel>
              <Select value={editCtx?.layer || 'Daily'} label="Layer" onChange={e => setEditCtx(prev => ({ ...prev, layer: e.target.value as ContextLayer }))}>
                {layers.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Key" fullWidth value={editCtx?.key || ''} onChange={e => setEditCtx(prev => ({ ...prev, key: e.target.value }))} placeholder="e.g., Current Focus, Team Priority" />
            <TextField label="Value" fullWidth multiline rows={4} value={editCtx?.value || ''} onChange={e => setEditCtx(prev => ({ ...prev, value: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Guided Setup Wizard */}
      <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Get Started with Context</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a layer, then select a suggestion to pre-fill a context entry. You can customise the value before saving.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Layer</InputLabel>
            <Select
              value={wizardLayer}
              label="Layer"
              onChange={e => setWizardLayer(e.target.value)}
            >
              {Object.keys(layerSuggestions).map(l => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Suggestions for {wizardLayer}</Typography>
          <List disablePadding>
            {(layerSuggestions[wizardLayer] || []).map(suggestion => (
              <ListItemButton
                key={suggestion.key}
                onClick={() => handleWizardSelect(suggestion)}
                sx={{ borderRadius: 1, mb: 0.5, border: '1px solid', borderColor: 'divider' }}
              >
                <ListItemText
                  primary={suggestion.key}
                  secondary={suggestion.value}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWizardOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
